import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { AuthProvider } from '@/context';
import { ToastContainer } from '@/components/ui/Toast';
import { AnalyticsRouteTracker } from '@/components/AnalyticsRouteTracker';
import { useAuth } from '@/hooks';
import { Landing } from './pages/Landing';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsAndConditions } from './pages/TermsAndConditions';
import { Waitlist } from './pages/Waitlist';
import { Onboarding } from './pages/Onboarding';
import { AdminShell } from '@/pages/admin/AdminShell';
import { TodayTab } from './pages/TodayTab';
import { Inbox } from './pages/Inbox';
import { ReviewV0Page } from './pages/review/ReviewV0Page';
import { isReviewV0Enabled } from '@/constants/featureFlags';
import { ScheduleTab } from './pages/ScheduleTab';
import {
  SettingsMain,
  SettingsProfile,
  SettingsHousehold,
  SettingsChildren,
  SettingsMembers,
  SettingsForwarding,
  SettingsApprovedSenders,
  SettingsNotifications,
  SettingsPlan,
} from './pages/settings';
import { Activity } from './pages/Activity';
import {
  TaskDeepLink,
  EventDeepLink,
  ScheduleDateDeepLink,
} from './pages/deeplink';
import { History } from './pages/History';
import { CreateHouseholdFlow } from './pages/CreateHouseholdFlow';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppShell } from './components/Layout/shared/AppShell';
import { ErrorBoundary } from '@/lib/sentry';

/**
 * `react-router-dom`'s <Navigate> discards the current search string.
 * This wrapper preserves `?foo=bar&…` across a redirect so deep links
 * like `/signup?invited=true` keep their flag once the user lands on
 * `/`. Use only for routes that should behave like a simple rename,
 * not for routes that want to reset query state.
 */
function RedirectPreservingSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return (
    <Navigate
      to={{ pathname: to, search }}
      replace
    />
  );
}

/**
 * Catch-all fallback for unknown routes. Authenticated users go into the
 * app (`/today`) rather than the marketing page — this prevents stranding
 * a logged-in user on `/landing` after an OAuth callback that lands on a
 * URL we don't have an explicit route for.
 */
function CatchAllFallback() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return (
    <Navigate
      to={user ? '/today' : '/landing'}
      replace
    />
  );
}

function App() {
  return (
    <ErrorBoundary fallback={<p>Something went wrong.</p>}>
      <AuthProvider>
        <ToastContainer />
        <BrowserRouter>
          <AnalyticsRouteTracker />
          <Routes>
            {/* Public routes */}
            {/* `/` is the auth entry point — StepWelcome (Google OAuth) for
              unauthenticated users, OnboardingFlow for authenticated-but-
              not-onboarded users, /today redirect for completed users. */}
            <Route
              path="/"
              element={<Onboarding />}
            />
            <Route
              path="/invite/:inviteToken"
              element={<Onboarding />}
            />
            {/* `/landing` is the marketing page (previously at `/`). */}
            <Route
              path="/landing"
              element={<Landing />}
            />
            <Route
              path="/privacy"
              element={<PrivacyPolicy />}
            />
            <Route
              path="/terms"
              element={<TermsAndConditions />}
            />
            <Route
              path="/waitlist"
              element={<Waitlist />}
            />
            <Route
              path="/admin"
              element={<AdminShell />}
            />
            {/* Protected routes - wrapped in AppShell for shared navigation */}
            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route
                path="/today"
                element={<TodayTab />}
              />
              <Route
                path="/activity"
                element={<Activity />}
              />
              {/* SMS digest per-item deep links. The URL contract
                (`/t/:taskId`, `/e/:eventId`) is shared with the digest edge
                function. Nesting them here means an unauthenticated hit goes
                through sign-in/onboarding and lands on `/today`. */}
              <Route
                path="/t/:taskId"
                element={<TaskDeepLink />}
              />
              <Route
                path="/e/:eventId"
                element={<EventDeepLink />}
              />
              {/* SMS digest day link. Short by design — every character
                  competes for an SMS segment. Contract shared with the
                  digest edge function: `/s/YYYY-MM-DD`. */}
              <Route
                path="/s/:date"
                element={<ScheduleDateDeepLink />}
              />
              <Route
                path="/review"
                element={isReviewV0Enabled() ? <ReviewV0Page /> : <Inbox />}
              />
              {/* /review/:emailId → redirect to /review (detail is now a modal) */}
              <Route
                path="/review/:emailId"
                element={
                  <Navigate
                    to="/review"
                    replace
                  />
                }
              />
              <Route
                path="/schedule"
                element={<ScheduleTab />}
              />
              <Route
                path="/history"
                element={<History />}
              />
              <Route
                path="/settings"
                element={<SettingsMain />}
              />
              <Route
                path="/settings/profile"
                element={<SettingsProfile />}
              />
              <Route
                path="/settings/household"
                element={<SettingsHousehold />}
              />
              <Route
                path="/settings/children"
                element={<SettingsChildren />}
              />
              <Route
                path="/settings/members"
                element={<SettingsMembers />}
              />
              <Route
                path="/settings/forwarding"
                element={<SettingsForwarding />}
              />
              <Route
                path="/settings/senders"
                element={<SettingsApprovedSenders />}
              />
              <Route
                path="/settings/notifications"
                element={<SettingsNotifications />}
              />
              <Route
                path="/create-household"
                element={<CreateHouseholdFlow />}
              />
              <Route
                path="/settings/plan"
                element={<SettingsPlan />}
              />
              {/*
              Legacy /billing route — Plan and Billing are the same
              concept in the product. Keep the redirect so old links
              (and any external "Manage subscription" emails) still
              land in the right place.
            */}
              <Route
                path="/billing"
                element={<RedirectPreservingSearch to="/settings/plan" />}
              />
            </Route>

            {/* Backwards compatibility redirects — preserve search string so
              `/signup?invited=true` keeps the invite flag through the
              redirect to `/` (the new auth entry point). */}
            <Route
              path="/login"
              element={<RedirectPreservingSearch to="/" />}
            />
            <Route
              path="/signup"
              element={<RedirectPreservingSearch to="/" />}
            />
            {/* Legacy `/onboarding` path — internal links and external users
              may still use it; redirect to `/` while preserving search. */}
            <Route
              path="/onboarding"
              element={<RedirectPreservingSearch to="/" />}
            />
            <Route
              path="/dashboard"
              element={
                <Navigate
                  to="/today"
                  replace
                />
              }
            />
            <Route
              path="/inbox"
              element={
                <Navigate
                  to="/review"
                  replace
                />
              }
            />
            <Route
              path="/inbox/:emailId"
              element={
                <Navigate
                  to="/review"
                  replace
                />
              }
            />

            {/* Catch all — authenticated users bounce into the app, anonymous
              visitors get the marketing landing page. Prevents stranding a
              just-authenticated user on /landing after OAuth callbacks. */}
            <Route
              path="*"
              element={<CatchAllFallback />}
            />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
